import type { Item, ItemInput, Supplier, SupplierInput } from '@iobiz/shared'
import { uuidv7 } from '@iobiz/shared'
import { dbFor, type LocalRow } from '../db.js'
import { enqueue, runCycle } from '../sync/engine.js'
import { getItemLocal, getSupplierLocal } from './catalog.local.js'

/**
 * Écritures du catalogue en mode local-first.
 *
 * Même mécanique que pour les clients : réplique d'abord, file d'attente
 * ensuite. On ne duplique pas la logique — seule la projection en colonnes
 * change d'une entité à l'autre.
 */

/**
 * Champs réellement modifiés, et eux seuls.
 *
 * ⚠️ Générique : les clés de `input` sont comparées à l'état précédent. C'est
 * ce qui permet à deux personnes de corriger le même article — l'une le prix,
 * l'autre le code-barres — sans que le serveur y voie une collision.
 */
export function diffOf<T extends Record<string, unknown>>(
  before: Record<string, unknown> | null | undefined,
  input: T,
): Record<string, unknown> {
  if (!before) return { ...input }
  const delta: Record<string, unknown> = {}
  for (const [key, value] of Object.entries(input)) {
    if (key === 'id') continue
    const old = before[key]
    if ((old ?? null) === null && (value ?? null) === null) continue
    if (JSON.stringify(old ?? null) !== JSON.stringify(value ?? null)) delta[key] = value
  }
  return delta
}

function itemRow(id: string, input: ItemInput, version: number, tauxTva: number): LocalRow {
  return {
    id,
    kind: input.kind,
    sku: input.sku ?? null,
    barcode: input.barcode ?? null,
    name: input.name,
    description: input.description ?? null,
    category_id: input.categoryId ?? null,
    supplier_id: input.supplierId ?? null,
    tax_id: input.taxId ?? null,
    // ⚠️ Le TAUX est écrit À CÔTÉ de son identifiant : la caisse hors ligne le
    // lit sans jointure. Sans lui, un article créé sans réseau se vendrait
    // sans TVA jusqu'à la synchronisation suivante.
    tax_rate: tauxTva,
    unit: input.unit,
    purchase_price: input.purchasePrice ?? 0,
    sale_price: input.salePrice ?? 0,
    currency_code: input.currencyCode ?? null,
    track_stock: input.trackStock ?? true,
    duration_minutes: input.durationMinutes ?? null,
    min_stock_level: input.minStockLevel ?? 0,
    status: input.status ?? 'active',
    // Vignette = premier visuel : les listes lisent `image_url`, pas le tableau.
    image_url: input.images?.[0] ?? input.imageUrl ?? null,
    images: input.images ?? [],
    version,
  }
}

function supplierRow(id: string, input: SupplierInput, version: number): LocalRow {
  return {
    id,
    code: input.code ?? null,
    company_name: input.companyName,
    contact_name: input.contactName ?? null,
    email: input.email ?? null,
    phone: input.phone ?? null,
    website: input.website ?? null,
    address_line1: input.addressLine1 ?? null,
    postal_code: input.postalCode ?? null,
    city: input.city ?? null,
    country_code: input.countryCode ?? null,
    tax_id: input.taxId ?? null,
    payment_terms_days: input.paymentTermsDays ?? 0,
    notes: input.notes ?? null,
    version,
  }
}

export async function saveItem(
  storeId: string,
  input: ItemInput,
  existing?: Item | null,
): Promise<Item> {
  const db = dbFor(storeId)
  const id = existing?.id ?? input.id ?? uuidv7()
  const delta = diffOf(existing as unknown as Record<string, unknown> | null, input)
  if (existing && Object.keys(delta).length === 0) return existing

  const now = new Date().toISOString()
  /** Taux résolu depuis la liste répliquée — la caisse n'a pas à joindre. */
  const tauxTva = input.taxId
    ? Number(
        ((await db.taxes.get(input.taxId)) as unknown as Record<string, unknown> | undefined)?.[
          'rate'
        ] ?? 0,
      )
    : 0

  await db.items.put({
    ...itemRow(id, input, existing?.version ?? 0, tauxTva),
    created_at: existing?.createdAt ?? now,
    updated_at: now,
    _dirty: 1,
    _localOnly: existing ? 0 : 1,
  })

  await enqueue(storeId, {
    entity: 'item',
    entityId: id,
    op: existing ? 'update' : 'create',
    payload: existing ? delta : { ...input, id },
    baseVersion: existing ? existing.version : null,
  })
  void runCycle(storeId)

  return (await getItemLocal(storeId, id))!
}

export async function removeItem(storeId: string, item: Item): Promise<void> {
  const db = dbFor(storeId)
  await db.items.delete(item.id)
  await enqueue(storeId, {
    entity: 'item',
    entityId: item.id,
    op: 'delete',
    payload: {},
    baseVersion: item.version,
  })
  void runCycle(storeId)
}

export async function saveSupplier(
  storeId: string,
  input: SupplierInput,
  existing?: Supplier | null,
): Promise<Supplier> {
  const db = dbFor(storeId)
  const id = existing?.id ?? input.id ?? uuidv7()
  const delta = diffOf(existing as unknown as Record<string, unknown> | null, input)
  if (existing && Object.keys(delta).length === 0) return existing

  const now = new Date().toISOString()
  await db.suppliers.put({
    ...supplierRow(id, input, existing?.version ?? 0),
    created_at: existing?.createdAt ?? now,
    updated_at: now,
    _dirty: 1,
    _localOnly: existing ? 0 : 1,
  })

  await enqueue(storeId, {
    entity: 'supplier',
    entityId: id,
    op: existing ? 'update' : 'create',
    payload: existing ? delta : { ...input, id },
    baseVersion: existing ? existing.version : null,
  })
  void runCycle(storeId)

  return (await getSupplierLocal(storeId, id))!
}

export async function removeSupplier(storeId: string, supplier: Supplier): Promise<void> {
  const db = dbFor(storeId)
  await db.suppliers.delete(supplier.id)
  await enqueue(storeId, {
    entity: 'supplier',
    entityId: supplier.id,
    op: 'delete',
    payload: {},
    baseVersion: supplier.version,
  })
  void runCycle(storeId)
}
